import React, { useState, useEffect } from 'react'
import {
  auth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from '../firebase/firebase'
import { PhoneAuthProvider, signInWithCredential } from 'firebase/auth' // ✅ Import explicitly
import { BACKEND_URI } from '../config'
import { Button } from '@mui/material'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'


const PhoneAuth = ({ phoneNumber }) => {
  const navigate = useNavigate()
  const [otp, setOtp] = useState('')
  const [verificationId, setVerificationId] = useState(null)
  const [message, setMessage] = useState('')
  const [resendDisabled, setResendDisabled] = useState(true)
  const [resendTimer, setResendTimer] = useState(45) // Initial countdown

    useEffect(() => {
      setTimeout(() => {
        sendOtp();
      }, 1000); // Delay by 1 second to ensure Firebase Auth is ready
    }, []);

    useEffect(() => {
        let interval
        if (resendDisabled) {
          interval = setInterval(() => {
            setResendTimer(prev => {
              if (prev <= 1) {
                clearInterval(interval)
                setResendDisabled(false) // Enable button when timer ends
                return 45 // Reset timer for next use
              }
              return prev - 1
            })
          }, 1000)
        }
        return () => clearInterval(interval) // Cleanup on unmount
      }, [resendDisabled]) // ✅ Only runs when resendDisabled changes
  

    const setupRecaptcha = () => {
      if (!auth) {
        console.error("Firebase Auth is undefined!");
        setMessage("Firebase Authentication is not initialized.");
        return;
      }
      
      if (!window.recaptchaVerifier) {
        auth.languageCode = "en"; // ✅ Set language code explicitly

        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",
          },
        );
      }
    }

  const sendOtp = async () => {
    try {
      if (!window.recaptchaVerifier) {
        setupRecaptcha();
      }

      const confirmationResult = await signInWithPhoneNumber(
        auth,
        `+1${phoneNumber}`,
        window.recaptchaVerifier
      )
      setVerificationId(confirmationResult.verificationId)
      setMessage('Verification Code sent successfully!')
    } catch (error) {
      console.error('Error sending OTP:', error)
      setMessage('Failed to send verification code.')
    }
  }

  const handleResendVerification = () => {
    sendOtp() // Call your API function to resend the email
    setResendDisabled(true) // Disable the button
    setResendTimer(45) // Reset countdown timer
  }

  // Verify OTP
  const verifyOtp = async () => {
    try {
      if (!verificationId) {
        throw new Error("Verification ID is missing. Please request a new OTP.");
      }
      if (!otp) {
        throw new Error("OTP is required.");
      }

      const credential = PhoneAuthProvider.credential(verificationId, otp) // ✅ Correct usage
      await signInWithCredential(auth, credential) // ✅ Corrected function usage

      const response = await fetch(`${BACKEND_URI}/api/verify-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phoneNumber }), // ✅ Fixed JSON structure
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data.error || 'An error occurred')
      }
      setMessage('Phone number verified successfully!\nAwaiting approval from admin.')
      toast.success('Phone number verified successfully!\nAwaiting approval from admin.')
      navigate('/', {})
    } catch (error) {
      console.error('Error verifying OTP:', error)
      setMessage(error.message || 'Invalid verification code. Try again.')
    }
  }


  return (
    <div style={styles.container}>
      <div style={styles.formContainer}>
        <h2>Phone Authentication</h2>
        <div id='recaptcha-container'></div>

        <>
          <input
            type='text'
            placeholder='Enter SMS verification code'
            value={otp}
            onChange={e => setOtp(e.target.value)}
            style={styles.input}
          />
          <button onClick={verifyOtp} style={styles.button}>
            Verify OTP
          </button>
          <Button
              onClick={handleResendVerification}
              variant='contained'
              // color="#fadac0"
              style={styles.resendButton}
              disabled={resendDisabled}
            >
              {resendDisabled
                ? `Resend in ${resendTimer}s`
                : 'Send Verification Code'}
            </Button>
          <div id="recaptcha-container"></div>
          <p>{message}</p>
        </>
      </div>
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f5f5f5',
  },
  formContainer: {
    backgroundColor: '#fff',
    padding: '2rem',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    width: '300px',
    textAlign: 'center',
  },
  nameContainer: {
    display: 'flex', // Enable flexbox
    justifyContent: 'space-between', // Add space between the inputs
    gap: '1rem', // Space between First Name and Last Name inputs
    marginBottom: '1rem', // Add space below the row
    marginTop: '1rem',
  },
  nameInputGroup: {
    flex: 1, // Make each input take equal space
    textAlign: 'left',
  },
  inputGroup: {
    marginBottom: '1rem',
    textAlign: 'left',
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '4px',
    border: '1px solid #ccc',
    marginTop: '0.5rem',
    boxSizing: 'border-box',
  },
  button: {
    marginTop: '1rem',
    marginBottom: "10px",
    width: '100%',
    padding: '0.75rem',
    borderRadius: '4px',
    border: 'none',
    backgroundColor: '#007BFF',
    color: '#fff',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  resendButton: {
    backgroundColor: '#fadac0',
  },
}

export default PhoneAuth
